import React, { useCallback, useEffect, useRef, useState } from 'react';
import { forwardRefWithCitrusTheme, withCitrusTheme } from '@gousto-internal/citrus-react';
import type { CSSObject } from '@emotion/react';
import { HOME_REDESIGN_BREAKPOINT } from '../tokens';
import { CarouselArrowButton } from '../CarouselArrowButton/CarouselArrowButton';

const EDGE_FADE = '40px';
const FADE_LEFT = `linear-gradient(90deg, transparent 0, #000 ${EDGE_FADE})`;
const FADE_RIGHT = `linear-gradient(90deg, #000 calc(100% - ${EDGE_FADE}), transparent 100%)`;
const DESKTOP = `@media (min-width: ${HOME_REDESIGN_BREAKPOINT})`;

const Root = withCitrusTheme('div', () => ({
  position: 'relative',
  width: '100%',
}));

type ScrollerStyleProps = React.JSX.IntrinsicElements['div'] & {
  scrollSnap?: boolean;
  scrollerStyle?: CSSObject;
};

const ScrollerElement = React.forwardRef<HTMLDivElement, ScrollerStyleProps>(
  function ScrollerElement(
    { scrollSnap: _scrollSnap, scrollerStyle: _scrollerStyle, ...rest },
    ref,
  ) {
    return <div ref={ref} {...rest} />;
  },
);

const BOTH = `${FADE_LEFT}, ${FADE_RIGHT}`;
const maskFor = (left: boolean, right: boolean): string | undefined => {
  if (left && right) return BOTH;
  if (left) return FADE_LEFT;
  if (right) return FADE_RIGHT;
  return undefined;
};

const fadeRules = (mediaWrap: boolean) => {
  const combos = [
    { l: true, r: true },
    { l: true, r: false },
    { l: false, r: true },
  ];
  const rules: CSSObject = {};
  combos.forEach(({ l, r }) => {
    const mask = maskFor(l, r);
    const selector = `&[data-fade-left="${l}"][data-fade-right="${r}"]`;
    const decl: CSSObject = { maskImage: mask, WebkitMaskImage: mask };
    rules[selector] = mediaWrap ? { [DESKTOP]: decl } : decl;
  });
  return rules;
};

const StyledScroller = forwardRefWithCitrusTheme<ScrollerStyleProps, HTMLDivElement>(
  ScrollerElement,
  (theme, props) => ({
    overflowX: 'auto',
    width: '100%',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(14),
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },
    '&[data-edge-fade="all"]': fadeRules(false),
    '&[data-edge-fade="desktop"]': fadeRules(true),
    ...(props.scrollSnap
      ? { scrollSnapType: 'x mandatory', [DESKTOP]: { scrollSnapType: 'none' } }
      : {}),
    ...props.scrollerStyle,
  }),
);

type TrackStyleProps = React.JSX.IntrinsicElements['div'] & {
  trackStyle?: CSSObject;
};

const Track = withCitrusTheme<'div', TrackStyleProps>(
  'div',
  (theme, props) => ({
    display: 'flex',
    gap: '14px',
    alignItems: 'stretch',
    boxSizing: 'border-box',
    width: 'max-content',
    margin: '0 auto',
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    ...props.trackStyle,
  }),
  { omitProps: ['trackStyle'] },
);

type TrackListStyleProps = React.JSX.IntrinsicElements['ul'] & {
  trackStyle?: CSSObject;
};

const TrackList = withCitrusTheme<'ul', TrackListStyleProps>(
  'ul',
  (theme, props) => ({
    display: 'flex',
    gap: '14px',
    alignItems: 'stretch',
    boxSizing: 'border-box',
    width: 'max-content',
    margin: '0 auto',
    padding: 0,
    listStyle: 'none',
    ...props.trackStyle,
  }),
  { omitProps: ['trackStyle'] },
);

const arrowPosition = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  display: 'none',
  [DESKTOP]: {
    display: 'inline-flex',
  },
} as const;

const LeftArrowSlot = withCitrusTheme('div', (theme) => ({
  ...arrowPosition,
  left: theme.spacing(2),
}));

const RightArrowSlot = withCitrusTheme('div', (theme) => ({
  ...arrowPosition,
  right: theme.spacing(2),
}));

interface RailProps {
  readonly children: React.ReactNode;
  readonly ariaLabel: string;
  readonly edgeFade?: 'all' | 'desktop' | 'none';
  readonly showArrows?: boolean;
  readonly trackAs?: 'div' | 'ul';
  readonly scrollSnap?: boolean;
  readonly scrollerStyle?: CSSObject;
  readonly trackStyle?: CSSObject;
}

export function Rail({
  children,
  ariaLabel,
  edgeFade = 'all',
  showArrows = false,
  trackAs = 'div',
  scrollSnap = false,
  scrollerStyle,
  trackStyle,
}: RailProps): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const tracksScroll = showArrows || edgeFade !== 'none';

  useEffect(() => {
    if (!tracksScroll) {
      return undefined;
    }
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }
    const update = () => {
      setCanScrollBack(el.scrollLeft > 1);
      setCanScrollForward(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [tracksScroll, children]);

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.8),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, []);

  return (
    <Root>
      <StyledScroller
        ref={scrollerRef}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        data-edge-fade={edgeFade}
        data-fade-left={canScrollBack}
        data-fade-right={canScrollForward}
        scrollSnap={scrollSnap}
        scrollerStyle={scrollerStyle}
      >
        {trackAs === 'ul' ? (
          <TrackList trackStyle={trackStyle}>{children}</TrackList>
        ) : (
          <Track trackStyle={trackStyle}>{children}</Track>
        )}
      </StyledScroller>
      {showArrows && canScrollBack && (
        <LeftArrowSlot>
          <CarouselArrowButton
            direction="prev"
            onClick={() => scrollByPage(-1)}
            aria-label={`Previous ${ariaLabel}`}
          />
        </LeftArrowSlot>
      )}
      {showArrows && canScrollForward && (
        <RightArrowSlot>
          <CarouselArrowButton
            direction="next"
            onClick={() => scrollByPage(1)}
            aria-label={`Next ${ariaLabel}`}
          />
        </RightArrowSlot>
      )}
    </Root>
  );
}
